<script setup>
defineOptions({ name: "SupportRemotoSection" });
import { ExclamationTriangleIcon } from "@heroicons/vue/24/outline";
import suporteImg from "@/assets/suporte.webp";

const remoteTools = [
  {
    name: "AnyDesk",
    description: "Ferramenta principal para sessões de suporte remoto.",
    cta: "Baixar AnyDesk",
    href: "https://anydesk.com/pt/downloads",
    note: "Instale antes de contatar o suporte para agilizar o atendimento.",
  },
  {
    name: "RustDesk",
    description: "Alternativa disponível quando solicitado pelo analista.",
    cta: "Baixar RustDesk",
    href: "https://rustdesk.com/",
    note: "Use somente quando solicitado pela equipe Kali.",
  },
];

// Revela o elemento com fade+slide assim que ele entra na viewport ao rolar a página.
const vReveal = {
  mounted(el) {
    el.classList.add("reveal-init");
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("reveal-visible");
          io.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" },
    );
    io.observe(el);
  },
};
</script>

<template>
  <section id="remoto" class="py-16 bg-slate-50 scroll-mt-24">
    <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
      <div v-reveal class="max-w-2xl">
        <h2
          class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]"
        >
          Suporte remoto
        </h2>
      </div>

      <div class="grid gap-10 mt-6 lg:grid-cols-2 lg:items-stretch">
        <div v-reveal class="flex flex-col h-full">
          <p class="text-lg text-gray-600">
            Quando necessário, nosso time pode acessar sua máquina com
            segurança para resolver o problema diretamente.
          </p>
          <p class="mt-4 text-sm text-gray-700">
            O analista vai te enviar o link e orientar o passo a passo
            durante o atendimento.
          </p>

          <div class="flex-1 mt-8 min-h-[12rem] rounded-2xl overflow-hidden">
            <img
              :src="suporteImg"
              alt="Suporte remoto Kali Sistemas"
              class="object-cover w-full h-full"
            />
          </div>
        </div>

        <div v-reveal>
          <div
            class="flex gap-3 items-start p-4 mb-6 text-sm text-amber-800 bg-amber-50 rounded-xl border border-amber-200"
          >
            <ExclamationTriangleIcon class="flex-shrink-0 w-5 h-5 text-amber-600" />
            <p>
              Só permita o acesso remoto quando você mesmo entrar em contato
              com o suporte da Kali. Nunca autorize sessões por iniciativa de
              terceiros.
            </p>
          </div>

          <div class="grid gap-6">
            <div
              v-for="tool in remoteTools"
              :key="tool.name"
              class="p-6 bg-[#e6f0f4] rounded-2xl"
            >
              <h3 class="font-semibold text-gray-900">{{ tool.name }}</h3>
              <p class="mt-1 text-sm text-gray-600">
                {{ tool.description }}
              </p>
              <p class="mt-2 text-xs italic text-gray-500">{{ tool.note }}</p>
              <a
                :href="tool.href"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center mt-4 text-sm font-semibold text-kali-blue-600 hover:underline"
              >
                {{ tool.cta }} →
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.reveal-init {
  opacity: 0;
  transform: translateY(28px);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;
}

.reveal-visible {
  opacity: 1;
  transform: translateY(0);
}

@media (prefers-reduced-motion: reduce) {
  .reveal-init {
    opacity: 1;
    transform: none;
    transition: none;
  }
}
</style>
